import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { auroraTopbarSx } from '../theme/auroraSurface';
import {
  Box, Drawer, AppBar, Toolbar, List, Typography, Divider, IconButton,
  ListItem, ListItemButton, ListItemIcon, ListItemText, CssBaseline,
  Avatar, Menu, MenuItem, Badge, Container, Grid, Card, CardContent,
  Paper, Button, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Alert, Snackbar, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Collapse, FormControlLabel, Switch, InputAdornment,
  Tooltip, useTheme, useMediaQuery, TablePagination, FormControl, InputLabel,
  Select, CircularProgress, Autocomplete
} from '@mui/material';
import {
  Menu as MenuIcon, Dashboard, Business, People, Event, Settings,
  Notifications, Search, AccountCircle, ChevronLeft, ChevronRight,
  Add, Edit, Block, CheckCircle, PersonAdd, Logout, School,
  CalendarToday, Groups, Refresh, Person, ExpandMore, ExpandLess,
  Sell, AdminPanelSettings, PersonSearch, Star, QuestionAnswer
} from '@mui/icons-material';
import StudentFormDialog from './librarian/StudentFormDialog';
import FeaturedLibrariesManagement from '../components/admin/FeaturedLibrariesManagement';
import LibraryLeadsManagement from '../components/admin/LibraryLeadsManagement';
import InstituteLeadsManagement from '../components/admin/InstituteLeadsManagement';
import { motion } from 'framer-motion';
import api from '../utils/api';
import ThemeToggle from '../components/ThemeToggle';
import { INDIAN_CITIES } from '../utils/indianCities';
import { AuthContext } from '../context/AuthContext';
import { GRADIENTS, RADIUS } from '../theme/tokens';

const drawerWidth = 280;

const MotionCard = motion(Card);
const MotionPaper = motion(Paper);

const AMENITIES_OPTIONS = [
  'WiFi', 'Air Conditioning', 'Parking', 'Cafeteria', 'Water Dispenser',
  'Locker Facility', 'Power Backup', 'CCTV', '24/7 Access', 'Study Rooms',
  'Discussion Rooms', 'Printing Services', 'Library', 'Whiteboard',
  'Bean Bags', 'Ergonomic Chairs', 'Natural Light', 'Silent Zone',
  'Charging Points', 'Stationery Store', 'Private Cabin', 'Open Cabin',
  'Books', 'Competitive Exam Books', 'Magazines', 'Children Books', 'Co-Working',
];

// Booking status values — must match the Booking model ENUM (backend/src/models/booking.js)
const BOOKING_STATUS_OPTIONS = ['pending', 'upcoming', 'active', 'completed', 'cancelled', 'declined', 'frozen', 'no_show'];

const getBookingStatusColor = (status) => {
  switch (status) {
    case 'active': return 'success';
    case 'upcoming': return 'primary';
    case 'pending': return 'warning';
    case 'completed': return 'info';
    case 'cancelled':
    case 'declined':
    case 'no_show': return 'error';
    case 'frozen': return 'secondary';
    default: return 'default';
  }
};

function SalesDashboardPage() {
  const { logout, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Layout state
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [currentView, setCurrentView] = useState('dashboard');
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);

  // Data state
  const [dashboardStats, setDashboardStats] = useState(null);
  const [libraries, setLibraries] = useState([]);
  const [librarians, setLibrarians] = useState([]);
  const [institutes, setInstitutes] = useState([]);
  const [instituteManagers, setInstituteManagers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Library state
  const [librarySearch, setLibrarySearch] = useState('');
  const [libraryPage, setLibraryPage] = useState(0);
  const [libraryRowsPerPage, setLibraryRowsPerPage] = useState(10);
  const [libraryTotal, setLibraryTotal] = useState(0);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [createLibraryOpen, setCreateLibraryOpen] = useState(false);
  const [editLibraryOpen, setEditLibraryOpen] = useState(false);
  const [selectedLibrary, setSelectedLibrary] = useState(null);
  const [newLibrary, setNewLibrary] = useState({ name: '', address: '', capacity: '', city: null, locality: '', website_enabled: false, lat: '', lng: '', amenities: [], booking_auto_approve: false, commission_rate: '' });
  const [editLibraryData, setEditLibraryData] = useState({ name: '', address: '', capacity: '', city: null, locality: '', website_enabled: false, lat: '', lng: '', amenities: [], booking_auto_approve: false, commission_rate: '' });

  // Librarian state
  const [librarianLoading, setLibrarianLoading] = useState(false);
  const [createLibrarianOpen, setCreateLibrarianOpen] = useState(false);
  const [assignLibrarianOpen, setAssignLibrarianOpen] = useState(false);
  const [selectedLibrarian, setSelectedLibrarian] = useState(null);
  const [newLibrarian, setNewLibrarian] = useState({ name: '', email: '', password: '', phone: '', library_id: '' });
  const [librarySearchOptions, setLibrarySearchOptions] = useState([]);
  const [librarySearchLoading, setLibrarySearchLoading] = useState(false);
  const [selectedAssignLibrary, setSelectedAssignLibrary] = useState(null);

  // Institute state
  const [instituteSearch, setInstituteSearch] = useState('');
  const [institutePage, setInstitutePage] = useState(0);
  const [instituteRowsPerPage, setInstituteRowsPerPage] = useState(10);
  const [instituteTotal, setInstituteTotal] = useState(0);
  const [instituteLoading, setInstituteLoading] = useState(false);
  const [createInstituteOpen, setCreateInstituteOpen] = useState(false);
  const [editInstituteOpen, setEditInstituteOpen] = useState(false);
  const [selectedInstitute, setSelectedInstitute] = useState(null);
  const [newInstitute, setNewInstitute] = useState({ name: '', address: '', city: null, contact_email: '', description: '', contact_phone: '', website: '', established_year: '', courses_offered: [], facilities: [], manager_name: '', manager_email: '', manager_phone: '', manager_password: '' });
  const [editInstituteData, setEditInstituteData] = useState({ name: '', address: '', city: null, contact_email: '', description: '', contact_phone: '', website: '', established_year: '', courses_offered: [], facilities: [] });

  // Institute Manager state
  const [instituteManagerLoading, setInstituteManagerLoading] = useState(false);
  const [createInstituteManagerOpen, setCreateInstituteManagerOpen] = useState(false);
  const [assignInstituteManagerOpen, setAssignInstituteManagerOpen] = useState(false);
  const [selectedInstituteManager, setSelectedInstituteManager] = useState(null);
  const [newInstituteManager, setNewInstituteManager] = useState({ name: '', email: '', password: '', phone: '', city: '', institute_id: '' });
  const [instituteSearchOptions, setInstituteSearchOptions] = useState([]);
  const [instituteSearchLoading, setInstituteSearchLoading] = useState(false);
  const [selectedAssignInstitute, setSelectedAssignInstitute] = useState(null);

  // Bookings state
  const [bookingSearch, setBookingSearch] = useState('');
  const [bookingStatus, setBookingStatus] = useState('');
  const [bookingPage, setBookingPage] = useState(0);
  const [bookingRowsPerPage, setBookingRowsPerPage] = useState(20);
  const [bookingTotal, setBookingTotal] = useState(0);
  const [bookingLoading, setBookingLoading] = useState(false);

  // Students state
  const [students, setStudents] = useState([]);
  const [studentSearch, setStudentSearch] = useState('');
  const [studentLibraryFilter, setStudentLibraryFilter] = useState('');
  const [studentStatusFilter, setStudentStatusFilter] = useState('');
  const [studentPage, setStudentPage] = useState(0);
  const [studentRowsPerPage, setStudentRowsPerPage] = useState(20);
  const [studentTotal, setStudentTotal] = useState(0);
  const [studentLoading, setStudentLoading] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentDetailOpen, setStudentDetailOpen] = useState(false);
  const [studentDetailLoading, setStudentDetailLoading] = useState(false);

  // Student form state (Add Student dialog)
  const [studentFormOpen, setStudentFormOpen] = useState(false);

  // Booking action state
  const [bookingActionLoading, setBookingActionLoading] = useState(null); // booking id currently loading
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [rescheduleBookingId, setRescheduleBookingId] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const [statusMenuBookingId, setStatusMenuBookingId] = useState(null);

  // Active Sessions state
  const [activeSessions, setActiveSessions] = useState([]);
  const [activeSessionsLoading, setActiveSessionsLoading] = useState(false);

  // Sidebar sections state
  const [expandedSections, setExpandedSections] = useState({
    'library-section': true,
    'institute-section': true,
    'booking-section': true,
  });

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const menuSections = [
    {
      id: 'main',
      items: [
        { id: 'dashboard', text: 'Dashboard', icon: <Dashboard /> },
      ]
    },
    {
      id: 'library-section',
      label: 'Library Management',
      icon: <Business />,
      items: [
        { id: 'libraries', text: 'Libraries', icon: <Business /> },
        { id: 'librarians', text: 'Librarians', icon: <Person /> },
        { id: 'featured-libraries', text: 'Featured Libraries', icon: <Star /> },
        { id: 'library-leads', text: 'Join Requests', icon: <QuestionAnswer /> },
      ]
    },
    {
      id: 'institute-section',
      label: 'Institute Management',
      icon: <School />,
      items: [
        { id: 'institutes', text: 'Institutes', icon: <School /> },
        { id: 'institute-managers', text: 'Managers', icon: <AdminPanelSettings /> },
        { id: 'institute-leads', text: 'Student Inquiries', icon: <QuestionAnswer /> },
      ]
    },
    {
      id: 'booking-section',
      label: 'Bookings & Sessions',
      icon: <Event />,
      items: [
        { id: 'bookings', text: 'All Bookings', icon: <Event /> },
        { id: 'sessions', text: 'Active Sessions', icon: <Groups /> },
      ]
    },
    {
      id: 'main-students',
      items: [
        { id: 'students', text: 'Students', icon: <PersonSearch /> },
      ]
    },
  ];

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  useEffect(() => {
    if (isMobile) setSidebarOpen(false);
    else setSidebarOpen(true);
  }, [isMobile]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // Fetch dashboard stats
  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const response = await api.get('/sales/dashboard');
      setDashboardStats(response.data.data.stats);
    } catch (err) {
      console.error('Error fetching dashboard:', err);
      showSnackbar('Failed to load dashboard', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch libraries
  const fetchLibraries = async () => {
    try {
      setLibraryLoading(true);
      const params = new URLSearchParams({ page: libraryPage + 1, limit: libraryRowsPerPage });
      if (librarySearch) params.append('search', librarySearch);
      const response = await api.get(`/sales/libraries?${params}`);
      setLibraries(response.data.data.libraries || []);
      setLibraryTotal(response.data.data.pagination?.total || 0);
    } catch (err) {
      console.error('Error fetching libraries:', err);
      showSnackbar('Failed to load libraries', 'error');
    } finally {
      setLibraryLoading(false);
    }
  };

  useEffect(() => {
    if (currentView === 'libraries') fetchLibraries();
    if (currentView === 'students' && libraries.length === 0) fetchLibraries();
  }, [currentView, libraryPage, libraryRowsPerPage]);

  // Fetch librarians
  const fetchLibrarians = async () => {
    try {
      setLibrarianLoading(true);
      const response = await api.get('/sales/librarians');
      setLibrarians(response.data.data.librarians || []);
    } catch (err) {
      console.error('Error fetching librarians:', err);
      showSnackbar('Failed to load librarians', 'error');
    } finally {
      setLibrarianLoading(false);
    }
  };

  useEffect(() => {
    if (currentView === 'librarians') fetchLibrarians();
  }, [currentView]);

  // Fetch institutes
  const fetchInstitutes = async () => {
    try {
      setInstituteLoading(true);
      const params = new URLSearchParams({ page: institutePage + 1, limit: instituteRowsPerPage });
      if (instituteSearch) params.append('search', instituteSearch);
      const response = await api.get(`/sales/institutes?${params}`);
      setInstitutes(response.data.data.institutes || []);
      setInstituteTotal(response.data.data.pagination?.total || 0);
    } catch (err) {
      console.error('Error fetching institutes:', err);
      showSnackbar('Failed to load institutes', 'error');
    } finally {
      setInstituteLoading(false);
    }
  };

  useEffect(() => {
    if (currentView === 'institutes') fetchInstitutes();
  }, [currentView, institutePage, instituteRowsPerPage]);

  // Fetch institute managers
  const fetchInstituteManagers = async () => {
    try {
      setInstituteManagerLoading(true);
      const response = await api.get('/sales/institute-managers');
      setInstituteManagers(response.data.data.managers || []);
    } catch (err) {
      console.error('Error fetching institute managers:', err);
      showSnackbar('Failed to load institute managers', 'error');
    } finally {
      setInstituteManagerLoading(false);
    }
  };

  useEffect(() => {
    if (currentView === 'institute-managers') fetchInstituteManagers();
  }, [currentView]);

  // Fetch bookings
  const fetchBookings = async () => {
    try {
      setBookingLoading(true);
      const params = new URLSearchParams({ page: bookingPage + 1, limit: bookingRowsPerPage });
      if (bookingSearch) params.append('search', bookingSearch);
      if (bookingStatus) params.append('status', bookingStatus);
      const response = await api.get(`/sales/bookings?${params}`);
      setBookings(response.data.data.bookings || []);
      setBookingTotal(response.data.data.pagination?.total || 0);
    } catch (err) {
      console.error('Error fetching bookings:', err);
      showSnackbar('Failed to load bookings', 'error');
    } finally {
      setBookingLoading(false);
    }
  };

  useEffect(() => {
    if (currentView === 'bookings') fetchBookings();
  }, [currentView, bookingPage, bookingRowsPerPage, bookingStatus]);

  // Fetch students
  const fetchStudents = async () => {
    try {
      setStudentLoading(true);
      const params = new URLSearchParams({ page: studentPage + 1, limit: studentRowsPerPage });
      if (studentSearch) params.append('search', studentSearch);
      if (studentLibraryFilter) params.append('library_id', studentLibraryFilter);
      if (studentStatusFilter) params.append('status', studentStatusFilter);
      const response = await api.get(`/sales/students?${params}`);
      setStudents(response.data.data.students || []);
      setStudentTotal(response.data.data.pagination?.total || 0);
    } catch (err) {
      console.error('Error fetching students:', err);
      showSnackbar('Failed to load students', 'error');
    } finally {
      setStudentLoading(false);
    }
  };

  useEffect(() => {
    if (currentView === 'students') fetchStudents();
  }, [currentView, studentPage, studentRowsPerPage, studentStatusFilter, studentLibraryFilter]);

  // Fetch active sessions
  const fetchActiveSessions = async () => {
    try {
      setActiveSessionsLoading(true);
      const response = await api.get('/sales/sessions/active');
      setActiveSessions(response.data?.data?.sessions || []);
    } catch (err) {
      console.error('Error fetching active sessions:', err);
      showSnackbar('Failed to load active sessions', 'error');
    } finally {
      setActiveSessionsLoading(false);
    }
  };

  useEffect(() => {
    if (currentView === 'sessions') fetchActiveSessions();
  }, [currentView]);

  const handleOpenStudentDetail = async (student) => {
    setStudentDetailOpen(true);
    setStudentDetailLoading(true);
    try {
      const response = await api.get(`/sales/students/${student.id}`);
      setSelectedStudent(response.data.data.student || student);
    } catch (err) {
      console.error('Error fetching student detail:', err);
      setSelectedStudent(student);
    } finally {
      setStudentDetailLoading(false);
    }
  };

  // Search libraries for assignment dropdown
  const searchLibrariesForAssignment = async (searchText) => {
    try {
      setLibrarySearchLoading(true);
      const params = new URLSearchParams({ limit: 50 });
      if (searchText) params.append('search', searchText);
      const response = await api.get(`/sales/libraries?${params}`);
      setLibrarySearchOptions(response.data.data.libraries || []);
    } catch (err) {
      console.error('Error searching libraries:', err);
    } finally {
      setLibrarySearchLoading(false);
    }
  };

  // Search institutes for assignment dropdown
  const searchInstitutesForAssignment = async (searchText) => {
    try {
      setInstituteSearchLoading(true);
      const params = new URLSearchParams({ limit: 50 });
      if (searchText) params.append('search', searchText);
      const response = await api.get(`/sales/institutes?${params}`);
      setInstituteSearchOptions(response.data.data.institutes || []);
    } catch (err) {
      console.error('Error searching institutes:', err);
    } finally {
      setInstituteSearchLoading(false);
    }
  };

  useEffect(() => {
    if (assignLibrarianOpen) {
      searchLibrariesForAssignment('');
      if (selectedLibrarian?.library) {
        setSelectedAssignLibrary(selectedLibrarian.library);
      } else {
        setSelectedAssignLibrary(null);
      }
    }
  }, [assignLibrarianOpen, selectedLibrarian]);

  useEffect(() => {
    if (assignInstituteManagerOpen) {
      searchInstitutesForAssignment('');
      if (selectedInstituteManager?.institute) {
        setSelectedAssignInstitute(selectedInstituteManager.institute);
      } else {
        setSelectedAssignInstitute(null);
      }
    }
  }, [assignInstituteManagerOpen, selectedInstituteManager]);

  // Library handlers
  const handleCreateLibrary = async () => {
    try {
      await api.post('/sales/libraries', {
        name: newLibrary.name,
        address: newLibrary.address,
        capacity: parseInt(newLibrary.capacity, 10) || 0,
        city: newLibrary.city || null,
        locality: newLibrary.locality || null,
        website_enabled: newLibrary.website_enabled,
        lat: newLibrary.lat !== '' ? parseFloat(newLibrary.lat) : null,
        lng: newLibrary.lng !== '' ? parseFloat(newLibrary.lng) : null,
        amenities: newLibrary.amenities,
        booking_auto_approve: newLibrary.booking_auto_approve,
        commission_rate: newLibrary.commission_rate !== '' ? parseFloat(newLibrary.commission_rate) : null,
      });
      setCreateLibraryOpen(false);
      setNewLibrary({ name: '', address: '', capacity: '', city: null, locality: '', website_enabled: false, lat: '', lng: '', amenities: [], booking_auto_approve: false, commission_rate: '' });
      showSnackbar('Library created successfully');
      fetchLibraries();
      fetchDashboardStats();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to create library', 'error');
    }
  };

  const handleEditLibrary = async () => {
    try {
      await api.put(`/sales/libraries/${selectedLibrary.id}`, {
        name: editLibraryData.name,
        address: editLibraryData.address,
        capacity: parseInt(editLibraryData.capacity, 10) || 0,
        city: editLibraryData.city || null,
        locality: editLibraryData.locality || null,
        website_enabled: editLibraryData.website_enabled,
        lat: editLibraryData.lat !== '' ? parseFloat(editLibraryData.lat) : null,
        lng: editLibraryData.lng !== '' ? parseFloat(editLibraryData.lng) : null,
        amenities: editLibraryData.amenities,
        booking_auto_approve: editLibraryData.booking_auto_approve,
        commission_rate: editLibraryData.commission_rate !== '' ? parseFloat(editLibraryData.commission_rate) : null,
      });
      setEditLibraryOpen(false);
      setSelectedLibrary(null);
      showSnackbar('Library updated successfully');
      fetchLibraries();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to update library', 'error');
    }
  };

  const openEditLibraryDialog = (library) => {
    setSelectedLibrary(library);
    setEditLibraryData({
      name: library.name,
      address: library.address,
      capacity: library.capacity || '',
      city: library.city || null,
      locality: library.locality || '',
      website_enabled: library.website_enabled || false,
      lat: library.lat != null ? String(library.lat) : '',
      lng: library.lng != null ? String(library.lng) : '',
      amenities: Array.isArray(library.amenities) ? library.amenities : [],
      booking_auto_approve: library.booking_auto_approve || false,
      commission_rate: library.commission_rate != null ? String(library.commission_rate) : '',
    });
    setEditLibraryOpen(true);
  };

  // Librarian handlers
  const handleCreateLibrarian = async () => {
    try {
      await api.post('/sales/librarians', {
        name: newLibrarian.name,
        email: newLibrarian.email,
        password: newLibrarian.password,
        phone: newLibrarian.phone || null,
        library_id: newLibrarian.library_id || null,
      });
      setCreateLibrarianOpen(false);
      setNewLibrarian({ name: '', email: '', password: '', phone: '', library_id: '' });
      showSnackbar('Librarian created successfully');
      fetchLibrarians();
      fetchDashboardStats();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to create librarian', 'error');
    }
  };

  const handleAssignLibrarian = async () => {
    try {
      await api.put(`/sales/librarians/${selectedLibrarian.id}/assign`, {
        library_id: selectedAssignLibrary?.id || null,
      });
      setAssignLibrarianOpen(false);
      setSelectedLibrarian(null);
      setSelectedAssignLibrary(null);
      showSnackbar('Librarian assignment updated');
      fetchLibrarians();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to assign librarian', 'error');
    }
  };

  // Institute handlers
  const handleCreateInstitute = async () => {
    try {
      await api.post('/sales/institutes', {
        name: newInstitute.name,
        address: newInstitute.address,
        city: newInstitute.city || null,
        contact_email: newInstitute.contact_email || null,
        description: newInstitute.description || null,
        contact_phone: newInstitute.contact_phone || null,
        website: newInstitute.website || null,
        established_year: newInstitute.established_year !== '' ? parseInt(newInstitute.established_year, 10) : null,
        courses_offered: newInstitute.courses_offered,
        facilities: newInstitute.facilities,
        manager_name: newInstitute.manager_name || null,
        manager_email: newInstitute.manager_email || null,
        manager_phone: newInstitute.manager_phone || null,
        manager_password: newInstitute.manager_password || null,
      });
      setCreateInstituteOpen(false);
      setNewInstitute({ name: '', address: '', city: null, contact_email: '', description: '', contact_phone: '', website: '', established_year: '', courses_offered: [], facilities: [], manager_name: '', manager_email: '', manager_phone: '', manager_password: '' });
      showSnackbar('Institute created successfully');
      fetchInstitutes();
      fetchDashboardStats();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to create institute', 'error');
    }
  };

  const handleEditInstitute = async () => {
    try {
      await api.put(`/sales/institutes/${selectedInstitute.id}`, {
        name: editInstituteData.name,
        address: editInstituteData.address,
        city: editInstituteData.city || null,
        contact_email: editInstituteData.contact_email || null,
        description: editInstituteData.description || null,
        contact_phone: editInstituteData.contact_phone || null,
        website: editInstituteData.website || null,
        established_year: editInstituteData.established_year !== '' ? parseInt(editInstituteData.established_year, 10) : null,
        courses_offered: editInstituteData.courses_offered,
        facilities: editInstituteData.facilities,
      });
      setEditInstituteOpen(false);
      setSelectedInstitute(null);
      showSnackbar('Institute updated successfully');
      fetchInstitutes();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to update institute', 'error');
    }
  };

  const openEditInstituteDialog = (institute) => {
    setSelectedInstitute(institute);
    setEditInstituteData({
      name: institute.name,
      address: institute.address,
      city: institute.city || null,
      contact_email: institute.contact_email || '',
      description: institute.description || '',
      contact_phone: institute.contact_phone || '',
      website: institute.website || '',
      established_year: institute.established_year != null ? String(institute.established_year) : '',
      courses_offered: Array.isArray(institute.courses_offered) ? institute.courses_offered : [],
      facilities: Array.isArray(institute.facilities) ? institute.facilities : [],
    });
    setEditInstituteOpen(true);
  };

  // Institute Manager handlers
  const handleCreateInstituteManager = async () => {
    try {
      await api.post('/sales/institute-managers', {
        name: newInstituteManager.name,
        email: newInstituteManager.email,
        password: newInstituteManager.password,
        phone: newInstituteManager.phone || null,
        city: newInstituteManager.city || null,
        institute_id: newInstituteManager.institute_id || null,
      });
      setCreateInstituteManagerOpen(false);
      setNewInstituteManager({ name: '', email: '', password: '', phone: '', city: '', institute_id: '' });
      showSnackbar('Institute manager created successfully');
      fetchInstituteManagers();
      fetchDashboardStats();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to create institute manager', 'error');
    }
  };

  const handleAssignInstituteManager = async () => {
    try {
      await api.put(`/sales/institute-managers/${selectedInstituteManager.id}/assign`, {
        institute_id: selectedAssignInstitute?.id || null,
      });
      setAssignInstituteManagerOpen(false);
      setSelectedInstituteManager(null);
      setSelectedAssignInstitute(null);
      showSnackbar('Institute manager assignment updated');
      fetchInstituteManagers();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to assign institute manager', 'error');
    }
  };

  // Booking action handlers
  // Raw status override (admin-parity) — sets any status with NO side effects
  // (no refunds, commission ledger, subscription, attendance, or notifications).
  const handleBookingStatusChange = async (bookingId, newStatus) => {
    setStatusMenuAnchor(null);
    setStatusMenuBookingId(null);
    try {
      await api.put(`/sales/bookings/${bookingId}/status`, { status: newStatus });
      setBookings((prev) => prev.map((b) => (b.id === bookingId ? { ...b, status: newStatus } : b)));
      showSnackbar(`Booking status updated to ${newStatus}`);
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to update booking status', 'error');
    }
  };

  const handleAcceptBooking = async (bookingId) => {
    try {
      setBookingActionLoading(bookingId);
      await api.put(`/sales/bookings/${bookingId}/accept`);
      showSnackbar('Booking accepted successfully');
      fetchBookings();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to accept booking', 'error');
    } finally {
      setBookingActionLoading(null);
    }
  };

  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm('Cancel this booking? This cannot be undone.')) return;
    try {
      setBookingActionLoading(bookingId);
      await api.post(`/sales/bookings/${bookingId}/cancel`);
      showSnackbar('Booking cancelled');
      fetchBookings();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to cancel booking', 'error');
    } finally {
      setBookingActionLoading(null);
    }
  };

  const handleOpenReschedule = (booking) => {
    setRescheduleBookingId(booking.id);
    setRescheduleDate(booking.start_date ? booking.start_date.split('T')[0] : '');
    setRescheduleOpen(true);
  };

  const handleRescheduleBooking = async () => {
    if (!rescheduleDate) {
      showSnackbar('Please select a new date', 'error');
      return;
    }
    try {
      setBookingActionLoading(rescheduleBookingId);
      await api.put(`/sales/bookings/${rescheduleBookingId}/reschedule`, { start_date: rescheduleDate });
      showSnackbar('Booking rescheduled successfully');
      setRescheduleOpen(false);
      setRescheduleBookingId(null);
      setRescheduleDate('');
      fetchBookings();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to reschedule booking', 'error');
    } finally {
      setBookingActionLoading(null);
    }
  };

  const handleMenuClick = (itemId) => {
    setCurrentView(itemId);
    if (isMobile) setSidebarOpen(false);
  };

  // Render Dashboard
  const renderDashboard = () => (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <Box mb={4}>
        <Typography variant="h4" fontWeight="800" gutterBottom
          sx={{
            background: GRADIENTS.navyLight,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>
          Welcome, {user?.name || 'Sales'}!
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage libraries, institutes, and bookings from your dashboard
        </Typography>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : dashboardStats && (
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <MotionCard
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1 }}
              sx={{ borderRadius: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
            >
              <CardContent sx={{ color: 'white' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h3" fontWeight="800">{dashboardStats?.libraries?.total ?? 0}</Typography>
                    <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>Total Libraries</Typography>
                  </Box>
                  <Business sx={{ fontSize: 48, opacity: 0.5 }} />
                </Box>
              </CardContent>
            </MotionCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <MotionCard
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              sx={{ borderRadius: 4, background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' }}
            >
              <CardContent sx={{ color: 'white' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h3" fontWeight="800">{dashboardStats?.users?.librarians ?? 0}</Typography>
                    <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>Total Librarians</Typography>
                  </Box>
                  <Person sx={{ fontSize: 48, opacity: 0.5 }} />
                </Box>
              </CardContent>
            </MotionCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <MotionCard
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              sx={{ borderRadius: 4, background: 'linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)' }}
            >
              <CardContent sx={{ color: 'white' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h3" fontWeight="800">{dashboardStats?.institutes?.total ?? 0}</Typography>
                    <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>Total Institutes</Typography>
                  </Box>
                  <School sx={{ fontSize: 48, opacity: 0.5 }} />
                </Box>
              </CardContent>
            </MotionCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <MotionCard
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              sx={{ borderRadius: 4, background: 'linear-gradient(135deg, #F093FB 0%, #F5576C 100%)' }}
            >
              <CardContent sx={{ color: 'white' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h3" fontWeight="800">{dashboardStats?.bookings?.total ?? 0}</Typography>
                    <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>Total Bookings</Typography>
                  </Box>
                  <Event sx={{ fontSize: 48, opacity: 0.5 }} />
                </Box>
              </CardContent>
            </MotionCard>
          </Grid>

          {/* Recent Activity */}
          <Grid item xs={12}>
            <MotionPaper
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
              sx={{ p: 3, borderRadius: 4 }}
            >
              <Typography variant="h6" fontWeight="700" gutterBottom>Quick Actions</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Business />}
                    onClick={() => { setCurrentView('libraries'); setCreateLibraryOpen(true); }}
                    sx={{ py: 2, borderRadius: 3 }}
                  >
                    Add Library
                  </Button>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<PersonAdd />}
                    onClick={() => { setCurrentView('librarians'); setCreateLibrarianOpen(true); }}
                    sx={{ py: 2, borderRadius: 3 }}
                  >
                    Add Librarian
                  </Button>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<School />}
                    onClick={() => { setCurrentView('institutes'); setCreateInstituteOpen(true); }}
                    sx={{ py: 2, borderRadius: 3 }}
                  >
                    Add Institute
                  </Button>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<AdminPanelSettings />}
                    onClick={() => { setCurrentView('institute-managers'); setCreateInstituteManagerOpen(true); }}
                    sx={{ py: 2, borderRadius: 3 }}
                  >
                    Add Manager
                  </Button>
                </Grid>
              </Grid>
            </MotionPaper>
          </Grid>
        </Grid>
      )}
    </motion.div>
  );

  // Render Libraries
  const renderLibraries = () => (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <Box mb={4} display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="h4" fontWeight="800" gutterBottom
            sx={{
              background: GRADIENTS.navyLight,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}>
            Library Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Create and manage libraries ({libraryTotal} total)
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setCreateLibraryOpen(true)}
          sx={{ borderRadius: 3, background: GRADIENTS.brand, px: 3 }}
        >
          Add Library
        </Button>
      </Box>

      <MotionPaper initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} sx={{ p: 3, mb: 3, borderRadius: 4 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search libraries..."
              value={librarySearch}
              onChange={(e) => setLibrarySearch(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && fetchLibraries()}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Button fullWidth variant="contained" onClick={() => { setLibraryPage(0); fetchLibraries(); }} sx={{ borderRadius: 2 }}>
              Search
            </Button>
          </Grid>
        </Grid>
      </MotionPaper>

      <MotionPaper initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.1 }} sx={{ borderRadius: 4, overflow: 'hidden' }}>
        {libraryLoading ? (
          <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: 'action.hover' }}>
                  <TableRow>
                    <TableCell><strong>Library</strong></TableCell>
                    <TableCell><strong>Address</strong></TableCell>
                    <TableCell><strong>City</strong></TableCell>
                    <TableCell><strong>Capacity</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {libraries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No libraries found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    libraries.map((library) => (
                      <TableRow key={library.id} hover>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={2}>
                            <Avatar sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                              {library.name?.charAt(0).toUpperCase()}
                            </Avatar>
                            <Typography fontWeight="600">{library.name}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{library.address}</TableCell>
                        <TableCell>{library.city || 'N/A'}</TableCell>
                        <TableCell>{library.capacity || 'N/A'}</TableCell>
                        <TableCell>
                          <Chip
                            label={library.active ? 'Active' : 'Inactive'}
                            color={library.active ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => openEditLibraryDialog(library)}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={libraryTotal}
              rowsPerPage={libraryRowsPerPage}
              page={libraryPage}
              onPageChange={(e, newPage) => setLibraryPage(newPage)}
              onRowsPerPageChange={(e) => { setLibraryRowsPerPage(parseInt(e.target.value, 10)); setLibraryPage(0); }}
            />
          </>
        )}
      </MotionPaper>
    </motion.div>
  );

  // Render Librarians
  const renderLibrarians = () => (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <Box mb={4} display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="h4" fontWeight="800" gutterBottom
            sx={{
              background: GRADIENTS.navyLight,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}>
            Librarian Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Create and assign librarians to libraries
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<PersonAdd />}
          onClick={() => setCreateLibrarianOpen(true)}
          sx={{ borderRadius: 3, background: GRADIENTS.brand, px: 3 }}
        >
          Add Librarian
        </Button>
      </Box>

      <MotionPaper initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} sx={{ borderRadius: 4, overflow: 'hidden' }}>
        {librarianLoading ? (
          <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: 'action.hover' }}>
                <TableRow>
                  <TableCell><strong>Librarian</strong></TableCell>
                  <TableCell><strong>Email</strong></TableCell>
                  <TableCell><strong>Phone</strong></TableCell>
                  <TableCell><strong>Assigned Library</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {librarians.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No librarians found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  librarians.map((librarian) => (
                    <TableRow key={librarian.id} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Avatar sx={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' }}>
                            {librarian.name?.charAt(0).toUpperCase()}
                          </Avatar>
                          <Typography fontWeight="600">{librarian.name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{librarian.email}</TableCell>
                      <TableCell>{librarian.phone || 'N/A'}</TableCell>
                      <TableCell>
                        {librarian.library ? (
                          <Chip label={librarian.library.name} color="primary" size="small" variant="outlined" />
                        ) : (
                          <Chip label="Not Assigned" color="default" size="small" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={librarian.blocked ? 'Blocked' : 'Active'}
                          color={librarian.blocked ? 'error' : 'success'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Assign Library">
                          <IconButton
                            size="small"
                            onClick={() => { setSelectedLibrarian(librarian); setAssignLibrarianOpen(true); }}
                          >
                            <Business fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </MotionPaper>
    </motion.div>
  );

  // Render Institutes
  const renderInstitutes = () => (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <Box mb={4} display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="h4" fontWeight="800" gutterBottom
            sx={{
              background: GRADIENTS.navyLight,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}>
            Institute Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Create and manage institutes ({instituteTotal} total)
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setCreateInstituteOpen(true)}
          sx={{ borderRadius: 3, background: GRADIENTS.brand, px: 3 }}
        >
          Add Institute
        </Button>
      </Box>

      <MotionPaper initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} sx={{ p: 3, mb: 3, borderRadius: 4 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search institutes..."
              value={instituteSearch}
              onChange={(e) => setInstituteSearch(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && fetchInstitutes()}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Button fullWidth variant="contained" onClick={() => { setInstitutePage(0); fetchInstitutes(); }} sx={{ borderRadius: 2 }}>
              Search
            </Button>
          </Grid>
        </Grid>
      </MotionPaper>

      <MotionPaper initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.1 }} sx={{ borderRadius: 4, overflow: 'hidden' }}>
        {instituteLoading ? (
          <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: 'action.hover' }}>
                  <TableRow>
                    <TableCell><strong>Institute</strong></TableCell>
                    <TableCell><strong>Address</strong></TableCell>
                    <TableCell><strong>City</strong></TableCell>
                    <TableCell><strong>Contact Email</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {institutes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No institutes found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    institutes.map((institute) => (
                      <TableRow key={institute.id} hover>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={2}>
                            <Avatar sx={{ background: 'linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)' }}>
                              {institute.name?.charAt(0).toUpperCase()}
                            </Avatar>
                            <Typography fontWeight="600">{institute.name}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{institute.address}</TableCell>
                        <TableCell>{institute.city || 'N/A'}</TableCell>
                        <TableCell>{institute.contact_email || 'N/A'}</TableCell>
                        <TableCell>
                          <Chip
                            label={institute.active ? 'Active' : 'Inactive'}
                            color={institute.active ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => openEditInstituteDialog(institute)}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={instituteTotal}
              rowsPerPage={instituteRowsPerPage}
              page={institutePage}
              onPageChange={(e, newPage) => setInstitutePage(newPage)}
              onRowsPerPageChange={(e) => { setInstituteRowsPerPage(parseInt(e.target.value, 10)); setInstitutePage(0); }}
            />
          </>
        )}
      </MotionPaper>
    </motion.div>
  );

  // Render Institute Managers
  const renderInstituteManagers = () => (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <Box mb={4} display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="h4" fontWeight="800" gutterBottom
            sx={{
              background: GRADIENTS.navyLight,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}>
            Institute Manager Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Create and assign institute managers
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<PersonAdd />}
          onClick={() => setCreateInstituteManagerOpen(true)}
          sx={{ borderRadius: 3, background: GRADIENTS.brand, px: 3 }}
        >
          Add Manager
        </Button>
      </Box>

      <MotionPaper initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} sx={{ borderRadius: 4, overflow: 'hidden' }}>
        {instituteManagerLoading ? (
          <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: 'action.hover' }}>
                <TableRow>
                  <TableCell><strong>Manager</strong></TableCell>
                  <TableCell><strong>Email</strong></TableCell>
                  <TableCell><strong>Phone</strong></TableCell>
                  <TableCell><strong>Assigned Institute</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {instituteManagers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No institute managers found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  instituteManagers.map((manager) => (
                    <TableRow key={manager.id} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Avatar sx={{ background: 'linear-gradient(135deg, #F093FB 0%, #F5576C 100%)' }}>
                            {manager.name?.charAt(0).toUpperCase()}
                          </Avatar>
                          <Typography fontWeight="600">{manager.name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{manager.email}</TableCell>
                      <TableCell>{manager.phone || 'N/A'}</TableCell>
                      <TableCell>
                        {manager.institute ? (
                          <Chip label={manager.institute.name} color="primary" size="small" variant="outlined" />
                        ) : (
                          <Chip label="Not Assigned" color="default" size="small" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={manager.blocked ? 'Blocked' : 'Active'}
                          color={manager.blocked ? 'error' : 'success'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Assign Institute">
                          <IconButton
                            size="small"
                            onClick={() => { setSelectedInstituteManager(manager); setAssignInstituteManagerOpen(true); }}
                          >
                            <School fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </MotionPaper>
    </motion.div>
  );

  // Render Bookings
  const renderBookings = () => (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <Box mb={4}>
        <Typography variant="h4" fontWeight="800" gutterBottom
          sx={{
            background: GRADIENTS.navyLight,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>
          Bookings
        </Typography>
        <Typography variant="body1" color="text.secondary">
          View all bookings ({bookingTotal} total)
        </Typography>
      </Box>

      <MotionPaper initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} sx={{ p: 3, mb: 3, borderRadius: 4 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by student name..."
              value={bookingSearch}
              onChange={(e) => setBookingSearch(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && fetchBookings()}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select value={bookingStatus} label="Status" onChange={(e) => setBookingStatus(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={4}>
            <Button fullWidth variant="contained" onClick={() => { setBookingPage(0); fetchBookings(); }} sx={{ borderRadius: 2 }}>
              Search
            </Button>
          </Grid>
        </Grid>
      </MotionPaper>

      <MotionPaper initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.1 }} sx={{ borderRadius: 4, overflow: 'hidden' }}>
        {bookingLoading ? (
          <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: 'action.hover' }}>
                  <TableRow>
                    <TableCell><strong>Student</strong></TableCell>
                    <TableCell><strong>Library</strong></TableCell>
                    <TableCell><strong>Plan</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Payment</strong></TableCell>
                    <TableCell><strong>Start Date</strong></TableCell>
                    <TableCell><strong>End Date</strong></TableCell>
                    <TableCell><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bookings.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No bookings found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    bookings.map((booking) => {
                      const isTerminal = ['cancelled', 'completed', 'declined', 'no_show'].includes(booking.status);
                      const isActionLoading = bookingActionLoading === booking.id;
                      return (
                        <TableRow key={booking.id} hover>
                          <TableCell>{booking.student?.name || 'N/A'}</TableCell>
                          <TableCell>{booking.library?.name || 'N/A'}</TableCell>
                          <TableCell>
                            <Chip label={booking.plan?.plan_type || 'N/A'} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell>
                            <Tooltip title="Click to override status">
                              <Chip
                                label={booking.status}
                                size="small"
                                color={getBookingStatusColor(booking.status)}
                                onClick={(e) => {
                                  setStatusMenuAnchor(e.currentTarget);
                                  setStatusMenuBookingId(booking.id);
                                }}
                                onDelete={(e) => {
                                  setStatusMenuAnchor(e.currentTarget);
                                  setStatusMenuBookingId(booking.id);
                                }}
                                deleteIcon={<ExpandMore sx={{ fontSize: 14 }} />}
                                sx={{ fontWeight: 600, cursor: 'pointer' }}
                              />
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={booking.payment_status}
                              size="small"
                              color={booking.payment_status === 'paid' ? 'success' : 'warning'}
                            />
                          </TableCell>
                          <TableCell>{booking.start_date ? new Date(booking.start_date).toLocaleDateString() : 'N/A'}</TableCell>
                          <TableCell>{booking.end_date ? new Date(booking.end_date).toLocaleDateString() : 'N/A'}</TableCell>
                          <TableCell>
                            <Box display="flex" gap={0.5}>
                              {booking.status === 'pending' && (
                                <Tooltip title="Accept booking">
                                  <span>
                                    <IconButton
                                      size="small"
                                      color="success"
                                      disabled={isActionLoading}
                                      onClick={() => handleAcceptBooking(booking.id)}
                                      aria-label="Accept booking"
                                    >
                                      {isActionLoading ? <CircularProgress size={16} /> : <CheckCircle fontSize="small" />}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}
                              {!isTerminal && (
                                <Tooltip title="Cancel booking">
                                  <span>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      disabled={isActionLoading}
                                      onClick={() => handleCancelBooking(booking.id)}
                                      aria-label="Cancel booking"
                                    >
                                      <Block fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}
                              {(booking.status === 'pending' || booking.status === 'upcoming') && (
                                <Tooltip title="Reschedule booking">
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={isActionLoading}
                                      onClick={() => handleOpenReschedule(booking)}
                                      aria-label="Reschedule booking"
                                    >
                                      <CalendarToday fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[20, 50, 100]}
              component="div"
              count={bookingTotal}
              rowsPerPage={bookingRowsPerPage}
              page={bookingPage}
              onPageChange={(e, newPage) => setBookingPage(newPage)}
              onRowsPerPageChange={(e) => { setBookingRowsPerPage(parseInt(e.target.value, 10)); setBookingPage(0); }}
            />

            {/* Status override menu — anchored to the clicked status chip (admin-parity raw override) */}
            <Menu
              anchorEl={statusMenuAnchor}
              open={Boolean(statusMenuAnchor)}
              onClose={() => { setStatusMenuAnchor(null); setStatusMenuBookingId(null); }}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
              {BOOKING_STATUS_OPTIONS.map((s) => {
                const isCurrent = bookings.find((b) => b.id === statusMenuBookingId)?.status === s;
                return (
                  <MenuItem
                    key={s}
                    selected={isCurrent}
                    disabled={isCurrent}
                    onClick={() => handleBookingStatusChange(statusMenuBookingId, s)}
                    dense
                  >
                    <Chip label={s} size="small" color={getBookingStatusColor(s)} sx={{ fontWeight: 600, mr: 1, pointerEvents: 'none' }} />
                    {isCurrent && <Typography variant="caption" sx={{ ml: 'auto', pl: 1, opacity: 0.6 }}>current</Typography>}
                  </MenuItem>
                );
              })}
            </Menu>
          </>
        )}
      </MotionPaper>
    </motion.div>
  );

  // Render Students
  const renderStudents = () => (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <Box mb={4} display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="h4" fontWeight="800" gutterBottom
            sx={{
              background: GRADIENTS.navyLight,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}>
            Students
          </Typography>
          <Typography variant="body1" color="text.secondary">
            View all registered students ({studentTotal} total)
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<PersonAdd />}
          onClick={() => setStudentFormOpen(true)}
          sx={{ borderRadius: 3, background: GRADIENTS.brand, px: 3 }}
        >
          Add Student
        </Button>
      </Box>

      <MotionPaper initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} sx={{ p: 3, mb: 3, borderRadius: 4 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name, email, phone..."
              value={studentSearch}
              onChange={(e) => setStudentSearch(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && fetchStudents()}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Library</InputLabel>
              <Select value={studentLibraryFilter} label="Library" onChange={(e) => { setStudentLibraryFilter(e.target.value); setStudentPage(0); }}>
                <MenuItem value="">All Libraries</MenuItem>
                {libraries.map((lib) => (
                  <MenuItem key={lib.id} value={lib.id}>{lib.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select value={studentStatusFilter} label="Status" onChange={(e) => { setStudentStatusFilter(e.target.value); setStudentPage(0); }}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="blocked">Blocked</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button fullWidth variant="contained" onClick={() => { setStudentPage(0); fetchStudents(); }} sx={{ borderRadius: 2 }}>
              Search
            </Button>
          </Grid>
        </Grid>
      </MotionPaper>

      <MotionPaper initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.1 }} sx={{ borderRadius: 4, overflow: 'hidden' }}>
        {studentLoading ? (
          <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: 'action.hover' }}>
                  <TableRow>
                    <TableCell><strong>Student</strong></TableCell>
                    <TableCell><strong>Email</strong></TableCell>
                    <TableCell><strong>Phone</strong></TableCell>
                    <TableCell><strong>City</strong></TableCell>
                    <TableCell><strong>Origin</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Joined</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {students.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No students found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    students.map((student) => (
                      <TableRow
                        key={student.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleOpenStudentDetail(student)}
                      >
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={2}>
                            <Avatar sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', width: 32, height: 32, fontSize: '0.85rem' }}>
                              {student.name?.charAt(0).toUpperCase()}
                            </Avatar>
                            <Typography fontWeight="600">{student.name}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{student.email || 'N/A'}</TableCell>
                        <TableCell>{student.phone || 'N/A'}</TableCell>
                        <TableCell>{student.city || 'N/A'}</TableCell>
                        <TableCell>
                          {student.origin?.type === 'library' && student.origin?.library_name
                            ? <Chip label={student.origin.library_name} size="small" variant="outlined" />
                            : <Typography variant="body2" color="text.secondary">Direct</Typography>
                          }
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={student.blocked ? 'Blocked' : 'Active'}
                            color={student.blocked ? 'error' : 'success'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {student.created_at ? new Date(student.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[20, 50, 100]}
              component="div"
              count={studentTotal}
              rowsPerPage={studentRowsPerPage}
              page={studentPage}
              onPageChange={(e, newPage) => setStudentPage(newPage)}
              onRowsPerPageChange={(e) => { setStudentRowsPerPage(parseInt(e.target.value, 10)); setStudentPage(0); }}
            />
          </>
        )}
      </MotionPaper>
    </motion.div>
  );

  // Helper: format duration from entered_at to now
  const formatDuration = (enteredAt) => {
    if (!enteredAt) return '-';
    const diffMs = Date.now() - new Date(enteredAt).getTime();
    if (diffMs < 0) return '-';
    const totalMins = Math.floor(diffMs / 60000);
    const hours = Math.floor(totalMins / 60);
    const mins = totalMins % 60;
    if (hours === 0) return `${mins}m`;
    return `${hours}h ${mins}m`;
  };

  // Render Active Sessions
  const renderActiveSessions = () => (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <Box mb={4} display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="h4" fontWeight="800" gutterBottom
            sx={{
              background: GRADIENTS.navyLight,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}>
            Active Sessions
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Students currently checked in across all libraries ({activeSessions.length} active)
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={activeSessionsLoading ? <CircularProgress size={18} color="inherit" /> : <Refresh />}
          onClick={fetchActiveSessions}
          disabled={activeSessionsLoading}
          sx={{ borderRadius: 3, background: GRADIENTS.brand, px: 3 }}
        >
          Refresh
        </Button>
      </Box>

      <MotionPaper
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.1 }}
        sx={{ borderRadius: 4, overflow: 'hidden' }}
      >
        {activeSessionsLoading ? (
          <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: 'action.hover' }}>
                <TableRow>
                  <TableCell><strong>Student</strong></TableCell>
                  <TableCell><strong>Library</strong></TableCell>
                  <TableCell><strong>Check-in Time</strong></TableCell>
                  <TableCell><strong>Duration</strong></TableCell>
                  <TableCell><strong>Booking</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activeSessions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No active sessions right now</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  activeSessions.map((session) => (
                    <TableRow key={session.id} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Avatar sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', width: 32, height: 32, fontSize: '0.85rem' }}>
                            {(session.user?.name || '?').charAt(0).toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography fontWeight="600" variant="body2">{session.user?.name || 'N/A'}</Typography>
                            <Typography variant="caption" color="text.secondary">{session.user?.email || ''}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="600">{session.library?.name || 'N/A'}</Typography>
                        <Typography variant="caption" color="text.secondary">{session.library?.address || ''}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {session.entered_at
                            ? new Date(session.entered_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })
                            : '-'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {session.entered_at
                            ? new Date(session.entered_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
                            : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="600">
                          {formatDuration(session.entered_at)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {session.booking ? (
                          <Chip label="Booked" size="small" variant="outlined" color="primary" />
                        ) : (
                          <Chip label="Walk-in" size="small" variant="outlined" color="default" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip label="Active" size="small" color="success" />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </MotionPaper>
    </motion.div>
  );

  const renderContent = () => {
    switch (currentView) {
      case 'dashboard': return renderDashboard();
      case 'libraries': return renderLibraries();
      case 'librarians': return renderLibrarians();
      case 'institutes': return renderInstitutes();
      case 'institute-managers': return renderInstituteManagers();
      case 'bookings': return renderBookings();
      case 'students': return renderStudents();
      case 'featured-libraries': return <FeaturedLibrariesManagement apiBase="/sales" />;
      case 'library-leads': return <LibraryLeadsManagement apiBase="/sales" />;
      case 'institute-leads': return <InstituteLeadsManagement apiBase="/sales" />;
      case 'sessions': return renderActiveSessions();
      default: return renderDashboard();
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'transparent' }}>
      <CssBaseline />

      {/* App Bar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: sidebarOpen ? `calc(100% - ${drawerWidth}px)` : '100%' },
          ml: { md: sidebarOpen ? `${drawerWidth}px` : 0 },
          transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          ...auroraTopbarSx(theme),
          color: 'text.primary',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Toolbar>
          <IconButton edge="start" onClick={() => setSidebarOpen(!sidebarOpen)} sx={{ mr: 2 }}>
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" fontWeight="700" sx={{ flexGrow: 1 }}>
            Sales Portal
          </Typography>
          <ThemeToggle sx={{ mr: 1 }} />
          <Tooltip title="Account">
            <IconButton onClick={(e) => setUserMenuAnchor(e.currentTarget)}>
              <AccountCircle />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={userMenuAnchor}
            open={Boolean(userMenuAnchor)}
            onClose={() => setUserMenuAnchor(null)}
            PaperProps={{ sx: { borderRadius: 2, minWidth: 200 } }}
          >
            <MenuItem onClick={() => setUserMenuAnchor(null)}>
              <ListItemIcon><AccountCircle fontSize="small" /></ListItemIcon>
              <ListItemText>{user?.name || 'Sales User'}</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => { setUserMenuAnchor(null); navigate('/login'); setTimeout(() => logout(), 0); }}>
              <ListItemIcon><Logout fontSize="small" color="error" /></ListItemIcon>
              <ListItemText>Logout</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            borderRight: 'none',
            background: GRADIENTS.navyLight,
            color: 'white',
          },
        }}
      >
        <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <Avatar sx={{ background: GRADIENTS.brand, width: 48, height: 48 }}>
            <Sell />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight="700">Sales Portal</Typography>
            <Typography variant="caption" sx={{ opacity: 0.7 }}>{user?.name || 'Sales User'}</Typography>
          </Box>
        </Box>

        <List sx={{ px: 2, py: 1 }}>
          {menuSections.map((section) => (
            <React.Fragment key={section.id}>
              {section.label ? (
                <>
                  <ListItemButton
                    onClick={() => toggleSection(section.id)}
                    sx={{ borderRadius: 2, mb: 0.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
                  >
                    <ListItemIcon sx={{ color: 'rgba(255,255,255,0.7)', minWidth: 40 }}>
                      {section.icon}
                    </ListItemIcon>
                    <ListItemText primary={section.label} primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }} />
                    {expandedSections[section.id] ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                  <Collapse in={expandedSections[section.id]} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {section.items.map((item) => (
                        <ListItemButton
                          key={item.id}
                          selected={currentView === item.id}
                          onClick={() => handleMenuClick(item.id)}
                          sx={{
                            borderRadius: 2,
                            mb: 0.5,
                            ml: 2,
                            '&.Mui-selected': { bgcolor: 'rgba(242, 101, 34, 0.3)', '&:hover': { bgcolor: 'rgba(242, 101, 34, 0.4)' } },
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
                          }}
                        >
                          <ListItemIcon sx={{ color: currentView === item.id ? 'primary.main' : 'rgba(255,255,255,0.7)', minWidth: 40 }}>
                            {item.icon}
                          </ListItemIcon>
                          <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: '0.85rem' }} />
                        </ListItemButton>
                      ))}
                    </List>
                  </Collapse>
                </>
              ) : (
                section.items.map((item) => (
                  <ListItemButton
                    key={item.id}
                    selected={currentView === item.id}
                    onClick={() => handleMenuClick(item.id)}
                    sx={{
                      borderRadius: 2,
                      mb: 0.5,
                      '&.Mui-selected': { bgcolor: 'rgba(242, 101, 34, 0.3)', '&:hover': { bgcolor: 'rgba(242, 101, 34, 0.4)' } },
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
                    }}
                  >
                    <ListItemIcon sx={{ color: currentView === item.id ? 'primary.main' : 'rgba(255,255,255,0.7)', minWidth: 40 }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText primary={item.text} primaryTypographyProps={{ fontWeight: 600 }} />
                  </ListItemButton>
                ))
              )}
            </React.Fragment>
          ))}
        </List>
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: sidebarOpen ? `calc(100% - ${drawerWidth}px)` : '100%' },
          ml: { md: sidebarOpen ? 0 : `-${drawerWidth}px` },
          transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar />
        <Container maxWidth="xl">
          {renderContent()}
        </Container>
      </Box>

      {/* Dialogs */}
      {/* Create Library Dialog */}
      <Dialog open={createLibraryOpen} onClose={() => setCreateLibraryOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Library</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Library Name *" value={newLibrary.name} onChange={(e) => setNewLibrary({ ...newLibrary, name: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Address *" value={newLibrary.address} onChange={(e) => setNewLibrary({ ...newLibrary, address: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                options={INDIAN_CITIES}
                value={newLibrary.city}
                onChange={(e, value) => setNewLibrary({ ...newLibrary, city: value })}
                renderInput={(params) => <TextField {...params} label="City" />}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Capacity" type="number" value={newLibrary.capacity} onChange={(e) => setNewLibrary({ ...newLibrary, capacity: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Locality / Area"
                value={newLibrary.locality}
                onChange={(e) => setNewLibrary({ ...newLibrary, locality: e.target.value })}
                placeholder="e.g. Uttam Nagar"
                inputProps={{ maxLength: 100 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Latitude"
                type="number"
                value={newLibrary.lat}
                onChange={(e) => setNewLibrary({ ...newLibrary, lat: e.target.value })}
                inputProps={{ step: 'any' }}
                placeholder="e.g. 28.6139"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Longitude"
                type="number"
                value={newLibrary.lng}
                onChange={(e) => setNewLibrary({ ...newLibrary, lng: e.target.value })}
                inputProps={{ step: 'any' }}
                placeholder="e.g. 77.2090"
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={AMENITIES_OPTIONS}
                value={newLibrary.amenities}
                onChange={(e, value) => setNewLibrary({ ...newLibrary, amenities: value })}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip key={option} label={option} size="small" {...getTagProps({ index })} />
                  ))
                }
                renderInput={(params) => <TextField {...params} label="Amenities" placeholder="Select amenities" />}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Commission override (%)"
                type="number"
                value={newLibrary.commission_rate}
                onChange={(e) => setNewLibrary({ ...newLibrary, commission_rate: e.target.value })}
                inputProps={{ min: 0, max: 100, step: 0.1 }}
                helperText="Blank = inherit platform default"
              />
            </Grid>
            <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={<Switch checked={newLibrary.booking_auto_approve} onChange={(e) => setNewLibrary({ ...newLibrary, booking_auto_approve: e.target.checked })} />}
                label="Auto-approve bookings"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={newLibrary.website_enabled} onChange={(e) => setNewLibrary({ ...newLibrary, website_enabled: e.target.checked })} />}
                label="Enable Website Listing"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateLibraryOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateLibrary}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Library Dialog */}
      <Dialog open={editLibraryOpen} onClose={() => setEditLibraryOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Library</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Library Name *" value={editLibraryData.name} onChange={(e) => setEditLibraryData({ ...editLibraryData, name: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Address *" value={editLibraryData.address} onChange={(e) => setEditLibraryData({ ...editLibraryData, address: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                options={INDIAN_CITIES}
                value={editLibraryData.city}
                onChange={(e, value) => setEditLibraryData({ ...editLibraryData, city: value })}
                renderInput={(params) => <TextField {...params} label="City" />}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Capacity" type="number" value={editLibraryData.capacity} onChange={(e) => setEditLibraryData({ ...editLibraryData, capacity: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Locality / Area"
                value={editLibraryData.locality}
                onChange={(e) => setEditLibraryData({ ...editLibraryData, locality: e.target.value })}
                placeholder="e.g. Uttam Nagar"
                inputProps={{ maxLength: 100 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Latitude"
                type="number"
                value={editLibraryData.lat}
                onChange={(e) => setEditLibraryData({ ...editLibraryData, lat: e.target.value })}
                inputProps={{ step: 'any' }}
                placeholder="e.g. 28.6139"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Longitude"
                type="number"
                value={editLibraryData.lng}
                onChange={(e) => setEditLibraryData({ ...editLibraryData, lng: e.target.value })}
                inputProps={{ step: 'any' }}
                placeholder="e.g. 77.2090"
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={AMENITIES_OPTIONS}
                value={editLibraryData.amenities}
                onChange={(e, value) => setEditLibraryData({ ...editLibraryData, amenities: value })}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip key={option} label={option} size="small" {...getTagProps({ index })} />
                  ))
                }
                renderInput={(params) => <TextField {...params} label="Amenities" placeholder="Select amenities" />}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Commission override (%)"
                type="number"
                value={editLibraryData.commission_rate}
                onChange={(e) => setEditLibraryData({ ...editLibraryData, commission_rate: e.target.value })}
                inputProps={{ min: 0, max: 100, step: 0.1 }}
                helperText={
                  selectedLibrary?.commission_rate_effective != null
                    ? `Effective: ${selectedLibrary.commission_rate_effective}%`
                    : 'Blank = inherit platform default'
                }
              />
            </Grid>
            <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={<Switch checked={editLibraryData.booking_auto_approve} onChange={(e) => setEditLibraryData({ ...editLibraryData, booking_auto_approve: e.target.checked })} />}
                label="Auto-approve bookings"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={editLibraryData.website_enabled} onChange={(e) => setEditLibraryData({ ...editLibraryData, website_enabled: e.target.checked })} />}
                label="Enable Website Listing"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditLibraryOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditLibrary}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Create Librarian Dialog */}
      <Dialog open={createLibrarianOpen} onClose={() => setCreateLibrarianOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Librarian</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Name *" value={newLibrarian.name} onChange={(e) => setNewLibrarian({ ...newLibrarian, name: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Email *" type="email" value={newLibrarian.email} onChange={(e) => setNewLibrarian({ ...newLibrarian, email: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Password *" type="password" value={newLibrarian.password} onChange={(e) => setNewLibrarian({ ...newLibrarian, password: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Phone" value={newLibrarian.phone} onChange={(e) => setNewLibrarian({ ...newLibrarian, phone: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateLibrarianOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateLibrarian}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* Assign Librarian Dialog */}
      <Dialog open={assignLibrarianOpen} onClose={() => setAssignLibrarianOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Librarian to Library</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Assigning: <strong>{selectedLibrarian?.name}</strong>
            </Typography>
            <Autocomplete
              options={librarySearchOptions}
              getOptionLabel={(option) => option.name || ''}
              value={selectedAssignLibrary}
              onChange={(e, value) => setSelectedAssignLibrary(value)}
              onInputChange={(e, value) => searchLibrariesForAssignment(value)}
              loading={librarySearchLoading}
              renderInput={(params) => <TextField {...params} label="Select Library" placeholder="Search libraries..." />}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignLibrarianOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAssignLibrarian}>
            {selectedAssignLibrary ? 'Assign' : 'Unassign'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Institute Dialog */}
      <Dialog open={createInstituteOpen} onClose={() => setCreateInstituteOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New Institute</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Institute Details</Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Institute Name *" value={newInstitute.name} onChange={(e) => setNewInstitute({ ...newInstitute, name: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Address *" value={newInstitute.address} onChange={(e) => setNewInstitute({ ...newInstitute, address: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                options={INDIAN_CITIES}
                value={newInstitute.city}
                onChange={(e, value) => setNewInstitute({ ...newInstitute, city: value })}
                renderInput={(params) => <TextField {...params} label="City" />}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Contact Email" type="email" value={newInstitute.contact_email} onChange={(e) => setNewInstitute({ ...newInstitute, contact_email: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Contact Phone" value={newInstitute.contact_phone} onChange={(e) => setNewInstitute({ ...newInstitute, contact_phone: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Established Year"
                type="number"
                value={newInstitute.established_year}
                onChange={(e) => setNewInstitute({ ...newInstitute, established_year: e.target.value })}
                inputProps={{ min: 1800, max: 2100 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Website" value={newInstitute.website} onChange={(e) => setNewInstitute({ ...newInstitute, website: e.target.value })} placeholder="https://example.com" />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={newInstitute.description}
                onChange={(e) => setNewInstitute({ ...newInstitute, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={newInstitute.courses_offered}
                onChange={(e, value) => setNewInstitute({ ...newInstitute, courses_offered: value })}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip key={option} label={option} size="small" {...getTagProps({ index })} />
                  ))
                }
                renderInput={(params) => <TextField {...params} label="Courses Offered" placeholder="Type and press Enter" />}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={newInstitute.facilities}
                onChange={(e, value) => setNewInstitute({ ...newInstitute, facilities: value })}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip key={option} label={option} size="small" {...getTagProps({ index })} />
                  ))
                }
                renderInput={(params) => <TextField {...params} label="Facilities" placeholder="Type and press Enter" />}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}>
                <Typography variant="caption" color="text.secondary">Manager (optional)</Typography>
              </Divider>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Manager Name" value={newInstitute.manager_name} onChange={(e) => setNewInstitute({ ...newInstitute, manager_name: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Manager Email (optional)" type="email" value={newInstitute.manager_email} onChange={(e) => setNewInstitute({ ...newInstitute, manager_email: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Manager Phone" value={newInstitute.manager_phone} onChange={(e) => setNewInstitute({ ...newInstitute, manager_phone: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Manager Password (optional)" type="password" value={newInstitute.manager_password} onChange={(e) => setNewInstitute({ ...newInstitute, manager_password: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateInstituteOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateInstitute}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Institute Dialog */}
      <Dialog open={editInstituteOpen} onClose={() => setEditInstituteOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Institute</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Institute Name *" value={editInstituteData.name} onChange={(e) => setEditInstituteData({ ...editInstituteData, name: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Address *" value={editInstituteData.address} onChange={(e) => setEditInstituteData({ ...editInstituteData, address: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                options={INDIAN_CITIES}
                value={editInstituteData.city}
                onChange={(e, value) => setEditInstituteData({ ...editInstituteData, city: value })}
                renderInput={(params) => <TextField {...params} label="City" />}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Contact Email" type="email" value={editInstituteData.contact_email} onChange={(e) => setEditInstituteData({ ...editInstituteData, contact_email: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Contact Phone" value={editInstituteData.contact_phone} onChange={(e) => setEditInstituteData({ ...editInstituteData, contact_phone: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Established Year"
                type="number"
                value={editInstituteData.established_year}
                onChange={(e) => setEditInstituteData({ ...editInstituteData, established_year: e.target.value })}
                inputProps={{ min: 1800, max: 2100 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Website" value={editInstituteData.website} onChange={(e) => setEditInstituteData({ ...editInstituteData, website: e.target.value })} placeholder="https://example.com" />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={editInstituteData.description}
                onChange={(e) => setEditInstituteData({ ...editInstituteData, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={editInstituteData.courses_offered}
                onChange={(e, value) => setEditInstituteData({ ...editInstituteData, courses_offered: value })}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip key={option} label={option} size="small" {...getTagProps({ index })} />
                  ))
                }
                renderInput={(params) => <TextField {...params} label="Courses Offered" placeholder="Type and press Enter" />}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={editInstituteData.facilities}
                onChange={(e, value) => setEditInstituteData({ ...editInstituteData, facilities: value })}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip key={option} label={option} size="small" {...getTagProps({ index })} />
                  ))
                }
                renderInput={(params) => <TextField {...params} label="Facilities" placeholder="Type and press Enter" />}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditInstituteOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditInstitute}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Create Institute Manager Dialog */}
      <Dialog open={createInstituteManagerOpen} onClose={() => setCreateInstituteManagerOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Institute Manager</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Name *" value={newInstituteManager.name} onChange={(e) => setNewInstituteManager({ ...newInstituteManager, name: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Email *" type="email" value={newInstituteManager.email} onChange={(e) => setNewInstituteManager({ ...newInstituteManager, email: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Password *" type="password" value={newInstituteManager.password} onChange={(e) => setNewInstituteManager({ ...newInstituteManager, password: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Phone" value={newInstituteManager.phone} onChange={(e) => setNewInstituteManager({ ...newInstituteManager, phone: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                options={INDIAN_CITIES}
                value={newInstituteManager.city || null}
                onChange={(e, value) => setNewInstituteManager({ ...newInstituteManager, city: value || '' })}
                renderInput={(params) => <TextField {...params} label="City" />}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateInstituteManagerOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateInstituteManager}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* Assign Institute Manager Dialog */}
      <Dialog open={assignInstituteManagerOpen} onClose={() => setAssignInstituteManagerOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Manager to Institute</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Assigning: <strong>{selectedInstituteManager?.name}</strong>
            </Typography>
            <Autocomplete
              options={instituteSearchOptions}
              getOptionLabel={(option) => option.name || ''}
              value={selectedAssignInstitute}
              onChange={(e, value) => setSelectedAssignInstitute(value)}
              onInputChange={(e, value) => searchInstitutesForAssignment(value)}
              loading={instituteSearchLoading}
              renderInput={(params) => <TextField {...params} label="Select Institute" placeholder="Search institutes..." />}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignInstituteManagerOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAssignInstituteManager}>
            {selectedAssignInstitute ? 'Assign' : 'Unassign'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Student Detail Dialog (read-only) */}
      <Dialog open={studentDetailOpen} onClose={() => { setStudentDetailOpen(false); setSelectedStudent(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>Student Details</DialogTitle>
        <DialogContent>
          {studentDetailLoading ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          ) : selectedStudent && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Avatar sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', width: 56, height: 56, fontSize: '1.4rem' }}>
                  {selectedStudent.name?.charAt(0).toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="700">{selectedStudent.name}</Typography>
                  <Chip
                    label={selectedStudent.blocked ? 'Blocked' : 'Active'}
                    color={selectedStudent.blocked ? 'error' : 'success'}
                    size="small"
                  />
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Email</Typography>
                <Typography variant="body2">{selectedStudent.email || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Phone</Typography>
                <Typography variant="body2">{selectedStudent.phone || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Age</Typography>
                <Typography variant="body2">{selectedStudent.age || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">City</Typography>
                <Typography variant="body2">{selectedStudent.city || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Interested Courses</Typography>
                <Typography variant="body2">{selectedStudent.interested_courses || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Joined</Typography>
                <Typography variant="body2">
                  {selectedStudent.created_at
                    ? new Date(selectedStudent.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
                    : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Aadhaar on file</Typography>
                <Typography variant="body2">
                  <Chip
                    label={selectedStudent.has_aadhaar ? 'Yes' : 'No'}
                    color={selectedStudent.has_aadhaar ? 'success' : 'default'}
                    size="small"
                    variant="outlined"
                  />
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setStudentDetailOpen(false); setSelectedStudent(null); }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add Student Dialog */}
      <StudentFormDialog
        open={studentFormOpen}
        onClose={() => setStudentFormOpen(false)}
        student={null}
        apiPrefix="/sales"
        showSnackbar={showSnackbar}
        onSuccess={() => {
          setStudentFormOpen(false);
          fetchStudents();
        }}
      />

      {/* Reschedule Booking Dialog */}
      <Dialog open={rescheduleOpen} onClose={() => setRescheduleOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reschedule Booking</DialogTitle>
        <DialogContent>
          <TextField
            label="New Start Date"
            type="date"
            fullWidth
            value={rescheduleDate}
            onChange={(e) => setRescheduleDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: new Date().toISOString().split('T')[0] }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRescheduleOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleRescheduleBooking}
            disabled={!rescheduleDate || bookingActionLoading === rescheduleBookingId}
          >
            {bookingActionLoading === rescheduleBookingId ? <CircularProgress size={20} color="inherit" /> : 'Reschedule'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default SalesDashboardPage;
